import { SurveyForm } from '../../extViewmodels';

// LinearScale & NPS both render from `customForm.fields` as a bare `Integer` field; the scale
// bounds/labels + which type it is live on the matching `surveyForm` surveyPart, joined by fieldId.

export const NPS_DEFAULT_START_LABEL = 'Not at all likely';
export const NPS_DEFAULT_END_LABEL = 'Extremely likely';
export const NPS_MIN = 0;
export const NPS_MAX = 10;
export const LINEAR_SCALE_DEFAULT_MIN = 1;
export const LINEAR_SCALE_DEFAULT_MAX = 5;

// NPS is comparable across accounts, so its prompt is not author-editable — computed from the
// survey's company name wherever shown, and normalized onto the stored label at save.
export const npsQuestionPrompt = (companyName?: string | null): string =>
	`How likely are you to recommend ${companyName?.trim() || 'us'} to a friend or colleague?`;

type ResolvedScaleKind = 'LinearScale' | 'Nps';

export interface ResolvedScaleQuestion {
	kind: ResolvedScaleKind;
	min: number;
	max: number;
	startLabel?: string;
	endLabel?: string;
}

type ScaleQuestionShape = {
	_type?: string;
	fieldId?: string;
	questions?: ScaleQuestionShape[];
	minValue?: number;
	maxValue?: number;
	startLabel?: string | null;
	endLabel?: string | null;
};

const findQuestionByFieldId = (surveyForm: SurveyForm, fieldId: string): ScaleQuestionShape | undefined => {
	for (const part of (surveyForm.surveyParts ?? []) as ScaleQuestionShape[]) {
		if (part._type === 'SurveySection') {
			const nested = (part.questions ?? []).find(q => q.fieldId === fieldId);
			if (nested) return nested;
		} else if (part.fieldId === fieldId) {
			return part;
		}
	}
	return undefined;
};

/** Normalized scale config for a field, or null if it isn't a scale question. NPS is forced to 0–10. */
export const resolveScaleQuestion = (
	surveyForm: SurveyForm | undefined,
	fieldId: string | undefined
): ResolvedScaleQuestion | null => {
	if (!surveyForm || !fieldId) return null;
	const question = findQuestionByFieldId(surveyForm, fieldId);
	if (!question) return null;

	if (question._type === 'NpsQuestion') {
		return {
			kind: 'Nps',
			min: NPS_MIN,
			max: NPS_MAX,
			startLabel: question.startLabel?.trim() || NPS_DEFAULT_START_LABEL,
			endLabel: question.endLabel?.trim() || NPS_DEFAULT_END_LABEL,
		};
	}

	if (question._type === 'LinearScaleQuestion') {
		// Number.isFinite (not typeof) so a NaN bound falls back instead of propagating NaN through the clamp.
		const rawMin = Number.isFinite(question.minValue) ? (question.minValue as number) : LINEAR_SCALE_DEFAULT_MIN;
		const rawMax = Number.isFinite(question.maxValue) ? (question.maxValue as number) : LINEAR_SCALE_DEFAULT_MAX;
		return {
			kind: 'LinearScale',
			// Clamp to BE-permitted ranges (start ∈ {0,1}, end ∈ [2,10]) against corrupt/legacy bounds.
			min: rawMin <= 0 ? 0 : 1,
			max: Math.min(10, Math.max(2, rawMax)),
			startLabel: question.startLabel?.trim() || undefined,
			endLabel: question.endLabel?.trim() || undefined,
		};
	}

	return null;
};
